import React, { useState } from 'react';
import { X } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import VehicleImageUploader from '@/components/admin/VehicleImageUploader';
import VehicleVideoUploader from '@/components/admin/VehicleVideoUploader';
import DescriptionPointsEditor, {
  cleanDescriptionPoints,
} from '@/components/admin/DescriptionPointsEditor';
import {
  galleryFromAssets,
  PLACEHOLDER_IMAGE_URL,
  placeholderImageAsset,
  realImageAssets,
} from '@/utils/vehicleImages';
import VinLookupButton from '@/components/admin/VinLookupButton';
import { adminDialogPanel, adminIconBtn, adminOverlay } from '@/components/admin/adminUi';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || '';

const inputCls =
  "w-full bg-white/5 border border-white/10 text-white text-base px-4 py-2.5 font-['Manrope'] focus:outline-none focus:border-blue-500/50";
const selectCls = `${inputCls} [&>option]:bg-gray-900 [&>option]:text-white`;
const labelCls = "block text-base font-['Manrope'] font-medium text-white/80 mb-2";

function stockFromVin(vin) {
  const cleaned = String(vin || '').trim().toUpperCase();
  if (cleaned.length < 6) return '';
  return cleaned.slice(-6);
}

const emptyForm = () => ({
  make: '',
  model: '',
  year: new Date().getFullYear(),
  price: '',
  price_not_decided: false,
  mileage: '',
  stockNumber: '',
  city_mpg: '',
  hwy_mpg: '',
  status: 'available',
  exterior_color: '',
  interior_color: '',
  fuel_type: 'Gasoline',
  transmission: 'Automatic',
  engine: '',
  vin: '',
  body_type: '',
  drivetrain: '',
  cylinders: '',
  trim: '',
  condition: '',
  features: '',
  featured: false,
  list_at_end: false,
});

export default function AddVehicleModal({ isOpen, onClose, onSuccess, token }) {
  const [formData, setFormData] = useState(emptyForm());
  const [imageAssets, setImageAssets] = useState([]);
  const [imagesPending, setImagesPending] = useState(false);
  const [videoAssets, setVideoAssets] = useState([]);
  const [descriptionPoints, setDescriptionPoints] = useState([]);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => {
      const nextValue = type === 'checkbox' ? checked : value;
      const next = {
        ...prev,
        [name]: name === 'vin' ? String(nextValue).toUpperCase() : nextValue,
      };
      if (name === 'vin') {
        const derived = stockFromVin(next.vin);
        const prevDerived = stockFromVin(prev.vin);
        const currentStock = String(prev.stockNumber || '').trim().toUpperCase();
        if (!currentStock || currentStock === prevDerived) {
          next.stockNumber = derived;
        }
      }
      return next;
    });
  };

  const applyVinLookup = (updater) => {
    setFormData((prev) => {
      const next = typeof updater === 'function' ? updater(prev) : updater;
      const derived = stockFromVin(next.vin || prev.vin);
      const prevDerived = stockFromVin(prev.vin);
      const currentStock = String(prev.stockNumber || '').trim().toUpperCase();
      if (derived && (!currentStock || currentStock === prevDerived)) {
        return { ...next, stockNumber: derived };
      }
      return next;
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    const photos = realImageAssets(imageAssets);
    if (!photos.length && !imagesPending) {
      toast.error('Please upload at least one vehicle photo');
      return;
    }

    setIsSubmitting(true);

    try {
      const featuresArray = formData.features
        .split(',')
        .map((f) => f.trim())
        .filter((f) => f);

      const pendingWithoutPhotos = imagesPending && !photos.length;
      const savedAssets = pendingWithoutPhotos ? [placeholderImageAsset()] : photos;
      const images = galleryFromAssets(savedAssets);
      const parseOptionalInt = (v) => {
        if (v === '' || v === null || v === undefined) return undefined;
        const n = parseInt(v, 10);
        return Number.isNaN(n) ? undefined : n;
      };

      const payload = {
        make: formData.make,
        model: formData.model,
        year: parseInt(formData.year, 10),
        price: formData.price_not_decided ? 0 : parseInt(formData.price, 10),
        price_not_decided: Boolean(formData.price_not_decided),
        mileage: parseInt(formData.mileage, 10),
        stockNumber: formData.stockNumber?.trim() || undefined,
        city_mpg: parseOptionalInt(formData.city_mpg),
        hwy_mpg: parseOptionalInt(formData.hwy_mpg),
        status: formData.status,
        exterior_color: formData.exterior_color,
        interior_color: formData.interior_color,
        fuel_type: formData.fuel_type,
        transmission: formData.transmission,
        engine: formData.engine,
        vin: formData.vin,
        body_type: formData.body_type?.trim() || undefined,
        drivetrain: formData.drivetrain?.trim() || undefined,
        cylinders: formData.cylinders?.trim() || undefined,
        trim: formData.trim?.trim() || undefined,
        condition: formData.condition?.trim() || undefined,
        features: featuresArray,
        description_points: cleanDescriptionPoints(descriptionPoints),
        featured: formData.featured,
        list_at_end: Boolean(formData.list_at_end),
        images_pending: pendingWithoutPhotos,
        image: images[0] || (pendingWithoutPhotos ? PLACEHOLDER_IMAGE_URL : undefined),
        images,
        image_assets: savedAssets,
        videos: galleryFromAssets(videoAssets),
        video_assets: videoAssets,
      };

      await axios.post(`${BACKEND_URL}/api/vehicles`, payload, {
        headers: { Authorization: `Bearer ${token}` },
      });

      toast.success('Vehicle added successfully!');
      onSuccess();
      setFormData(emptyForm());
      setImageAssets([]);
      setImagesPending(false);
      setVideoAssets([]);
      setDescriptionPoints([]);
    } catch (error) {
      const message = error.response?.data?.detail || 'Failed to add vehicle';
      toast.error(typeof message === 'string' ? message : 'Failed to add vehicle');
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={adminOverlay()}>
      <div className={adminDialogPanel('max-w-4xl')}>
        <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#0a0a0a] z-10">
          <h2 className="text-2xl font-['Unbounded'] font-bold text-white">
            Add New Vehicle
          </h2>
          <button type="button" onClick={onClose} className={adminIconBtn} aria-label="Close">
            <X className="w-6 h-6" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 items-start">
            <div className="space-y-4">
              <div>
                <label className={labelCls}>VIN *</label>
                <input type="text" name="vin" value={formData.vin} onChange={handleChange} required minLength="17" maxLength="17" className={`${inputCls} uppercase`} placeholder="4T1K61AK5PU123456" />
                <VinLookupButton
                  vin={formData.vin}
                  token={token}
                  formData={formData}
                  onApply={applyVinLookup}
                  disabled={isSubmitting}
                />
              </div>
              <div>
                <label className={labelCls}>Stock Number</label>
                <input
                  type="text"
                  name="stockNumber"
                  value={formData.stockNumber}
                  onChange={handleChange}
                  className={inputCls}
                  placeholder={stockFromVin(formData.vin) || 'Last 6 of VIN'}
                />
              </div>
              <div>
                <label className={labelCls}>Make *</label>
                <input type="text" name="make" value={formData.make} onChange={handleChange} required className={inputCls} placeholder="Toyota" />
              </div>
              <div>
                <label className={labelCls}>Model *</label>
                <input type="text" name="model" value={formData.model} onChange={handleChange} required className={inputCls} placeholder="Camry" />
              </div>
              <div>
                <label className={labelCls}>Trim</label>
                <input type="text" name="trim" value={formData.trim} onChange={handleChange} className={inputCls} placeholder="XSE, Limited, Sport..." />
              </div>
              <div>
                <label className={labelCls}>Year *</label>
                <input type="number" name="year" value={formData.year} onChange={handleChange} required min="1900" max="2030" className={inputCls} />
              </div>
              <div>
                <label className={labelCls}>Body Type</label>
                <input type="text" name="body_type" value={formData.body_type} onChange={handleChange} className={inputCls} placeholder="Sedan, SUV, Truck..." />
              </div>
              <div>
                <label className={labelCls}>Engine *</label>
                <input type="text" name="engine" value={formData.engine} onChange={handleChange} required className={inputCls} placeholder="2.5L 4-Cylinder" />
              </div>
              <div>
                <label className={labelCls}>Fuel Type *</label>
                <select name="fuel_type" value={formData.fuel_type} onChange={handleChange} required className={selectCls}>
                  <option value="Gasoline">Gasoline</option>
                  <option value="Hybrid">Hybrid</option>
                  <option value="Electric">Electric</option>
                  <option value="Diesel">Diesel</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Transmission *</label>
                <select name="transmission" value={formData.transmission} onChange={handleChange} required className={selectCls}>
                  <option value="Automatic">Automatic</option>
                  <option value="Manual">Manual</option>
                  <option value="CVT">CVT</option>
                  <option value="DCT">DCT</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Drivetrain</label>
                <select name="drivetrain" value={formData.drivetrain} onChange={handleChange} className={selectCls}>
                  <option value="">—</option>
                  <option value="FWD">FWD</option>
                  <option value="RWD">RWD</option>
                  <option value="AWD">AWD</option>
                  <option value="4WD">4WD</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Cylinders</label>
                <input type="text" name="cylinders" value={formData.cylinders} onChange={handleChange} className={inputCls} placeholder="4, 6, 8..." />
              </div>
              <div>
                <label className={labelCls}>Features (comma-separated)</label>
                <textarea
                  name="features"
                  value={formData.features}
                  onChange={handleChange}
                  rows="3"
                  className={`${inputCls} resize-none`}
                  placeholder="Panoramic Sunroof, JBL Audio, Wireless CarPlay, Heated Seats"
                />
              </div>
            </div>

            <div className="space-y-4">
              <div>
                <label className={labelCls}>Price ($) {formData.price_not_decided ? '' : '*'}</label>
                <input
                  type="number"
                  name="price"
                  value={formData.price}
                  onChange={handleChange}
                  required={!formData.price_not_decided}
                  disabled={formData.price_not_decided}
                  min="0"
                  className={`${inputCls} disabled:opacity-40`}
                  placeholder="32500"
                />
                <label className="mt-2 flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="price_not_decided"
                    checked={formData.price_not_decided}
                    onChange={handleChange}
                    className="w-4 h-4 bg-white/5 border border-white/10 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
                  />
                  <span className="text-base font-['Manrope'] text-white/80">Price Not Decided yet</span>
                </label>
              </div>
              <div>
                <label className={labelCls}>Mileage (mi) *</label>
                <input type="number" name="mileage" value={formData.mileage} onChange={handleChange} required min="0" className={inputCls} placeholder="12500" />
              </div>
              <div>
                <label className={labelCls}>Exterior Color *</label>
                <input type="text" name="exterior_color" value={formData.exterior_color} onChange={handleChange} required className={inputCls} placeholder="Pearl White" />
              </div>
              <div>
                <label className={labelCls}>Interior Color *</label>
                <input type="text" name="interior_color" value={formData.interior_color} onChange={handleChange} required className={inputCls} placeholder="Black Leather" />
              </div>
              <div>
                <label className={labelCls}>City MPG</label>
                <input type="number" name="city_mpg" value={formData.city_mpg} onChange={handleChange} min="1" className={inputCls} placeholder="e.g. 28" />
              </div>
              <div>
                <label className={labelCls}>Highway MPG</label>
                <input type="number" name="hwy_mpg" value={formData.hwy_mpg} onChange={handleChange} min="1" className={inputCls} placeholder="e.g. 36" />
              </div>
              <div>
                <label className={labelCls}>Vehicle Condition</label>
                <select name="condition" value={formData.condition} onChange={handleChange} className={selectCls}>
                  <option value="">—</option>
                  <option value="New">New</option>
                  <option value="Used">Used</option>
                  <option value="Certified Pre-Owned">Certified Pre-Owned</option>
                </select>
              </div>
              <div>
                <label className={labelCls}>Status *</label>
                <select name="status" value={formData.status} onChange={handleChange} required className={selectCls}>
                  <option value="available">Available</option>
                  <option value="pending">Pending</option>
                </select>
              </div>
            </div>
          </div>

          <VehicleImageUploader
            assets={imageAssets}
            onChange={setImageAssets}
            token={token}
            disabled={isSubmitting}
            imagesPending={imagesPending}
            onImagesPendingChange={setImagesPending}
          />

          <VehicleVideoUploader
            assets={videoAssets}
            onChange={setVideoAssets}
            token={token}
            disabled={isSubmitting}
          />

          <DescriptionPointsEditor
            points={descriptionPoints}
            onChange={setDescriptionPoints}
            disabled={isSubmitting}
          />

          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="featured"
              name="featured"
              checked={formData.featured}
              onChange={handleChange}
              className="w-5 h-5 bg-white/5 border border-white/10 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
            />
            <label htmlFor="featured" className="text-base font-['Manrope'] font-medium text-white/80 cursor-pointer">
              Mark as Featured Vehicle
            </label>
          </div>
          <div className="flex items-center gap-3">
            <input
              type="checkbox"
              id="list_at_end"
              name="list_at_end"
              checked={formData.list_at_end}
              onChange={handleChange}
              className="w-5 h-5 bg-white/5 border border-white/10 text-blue-500 focus:ring-blue-500 focus:ring-offset-0"
            />
            <label htmlFor="list_at_end" className="text-base font-['Manrope'] font-medium text-white/80 cursor-pointer">
              Add to end of list
            </label>
          </div>

          <div className="sticky bottom-0 bg-[#0a0a0a] flex flex-col sm:flex-row sm:items-center sm:justify-end gap-3 pt-4 pb-[max(1rem,env(safe-area-inset-bottom))] border-t border-white/10">
            <button type="button" onClick={onClose} className="min-h-11 px-6 py-2 border border-white/10 text-white/80 hover:bg-white/5 transition-colors font-['Manrope']">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="min-h-11 px-6 py-2 bg-blue-500 hover:bg-blue-600 text-white font-['Manrope'] font-semibold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSubmitting ? 'Adding...' : 'Add Vehicle'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
